import React, { useState } from "react";
import { motion } from "framer-motion";
import { ItemUI } from "../types/item";
import PriceInput from "../components/inputs/PriceInput";

// row padding from .item in ItemList.scss — animated to/from 0 so the row
// collapses fully
const ROW_PADDING = 0;

// added rows grow in; deleted rows slide left and fade while collapsing, so
// the rows below glide up instead of jumping. Enter avoids transforms: the
// edit modal opens inside the row and a transformed ancestor would break its
// position: fixed.
const rowMotion = {
  initial: { opacity: 0, height: 0, paddingTop: 0, paddingBottom: 0 },
  animate: {
    opacity: 1,
    height: "auto",
    paddingTop: ROW_PADDING,
    paddingBottom: ROW_PADDING,
  },
  exit: { opacity: 0, x: -24, height: 0, paddingTop: 0, paddingBottom: 0 },
  transition: { duration: 0.2, ease: "easeOut" },
} as const;

interface ItemRowShellProps {
  item: ItemUI;
  // "init": building the check (/), "split": the shared check (/:id)
  variant: "init" | "split";
  // the right side, placed straight into the row next to the name/price
  children: React.ReactNode;
  // things positioned outside the columns: the + buttons, modals
  overlays?: React.ReactNode;
  // the far-left column, before the name/price (split rows' checkbox)
  leading?: React.ReactNode;
}

// What both item rows share: the animated .item wrapper and the read-only
// name/price column. Each variant fills in the right side.
const ItemRowShell: React.FC<ItemRowShellProps> = ({
  item,
  variant,
  children,
  overlays,
  leading,
}) => {
  // clip only while growing/collapsing; otherwise the + buttons, which sit
  // half outside the row, would be cut off
  const [animating, setAnimating] = useState(false);

  return (
    <motion.div
      className={`item ${variant}-item${animating ? " animating" : ""}`}
      {...rowMotion}
      onAnimationStart={() => setAnimating(true)}
      onAnimationComplete={() => setAnimating(false)}
    >
      {leading}
      <div className="item-name-price">
        <span className="item-name-readonly">{item.name}</span>
        <PriceInput price={item.price} setPrice={() => {}} editable={false} />
      </div>
      {children}
      {overlays}
    </motion.div>
  );
};

export default ItemRowShell;
